import {CareJourneyDetailsResponse, TaskStepName} from '@/api/dto/care-journey';
import DisplayHelpers from '@/helpers/display_helpers';

const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export function createUlid(): string {
  let timestamp = Date.now();
  const time = Array<string>(10);
  for (let index = time.length - 1; index >= 0; index -= 1) {
    time[index] = ULID_ALPHABET[timestamp % 32];
    timestamp = Math.floor(timestamp / 32);
  }

  const random = crypto.getRandomValues(new Uint8Array(16));
  return time.join('') + Array.from(random, value => ULID_ALPHABET[value & 31]).join('');
}

function stringValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function schedulingData(
  careJourney: CareJourneyDetailsResponse,
  intakeData: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const preferenceKeys = [
    'time_of_day',
    'virtual_appointments_allowed',
    'days_and_time_that_do_or_do_not_work',
  ];
  const scheduleData = careJourney.steps
    .find(step => step.stepName === TaskStepName.SCHEDULED_APPOINTMENT)?.attempts.at(-1)?.data as
    Record<string, unknown> | undefined;
  return Object.fromEntries(preferenceKeys.map(key => [key, scheduleData?.[key] ?? intakeData?.[key]]));
}

export function secureEmailVariables(
  careJourney: CareJourneyDetailsResponse,
  patientEmail: string,
  intakeData?: Record<string, unknown>,
): Record<string, string> {
  const {patient, patientPhoneNumbers, currentInsurancePlans} = careJourney.clientProfile;
  const activePlans = currentInsurancePlans?.filter(plan => !plan.canceled) ?? [];
  const insurancePlan = activePlans.find(plan => plan.selected) ?? activePlans[0];
  const preferences = schedulingData(careJourney, intakeData);
  const virtualAllowed = preferences.virtual_appointments_allowed;

  return {
    patient_name: DisplayHelpers.formatName(patient.firstName, undefined, patient.lastName),
    patient_dob: DisplayHelpers.formatDateFromDateOnlyString(patient.dateOfBirth),
    patient_phone: DisplayHelpers.formatPatientPhoneNumber(patientPhoneNumbers[0]) || patient.phone || '',
    patient_address: DisplayHelpers.formatAddress(patient.address),
    client_id: patient.clientId || '',
    patient_email: patientEmail,
    insurance_plan_name: insurancePlan?.insurancePlanName || insurancePlan?.insuranceCompanyName || '',
    insurance_id: insurancePlan?.value || '',
    pref_time_of_day: preferences.time_of_day
      ? DisplayHelpers.humanize(stringValue(preferences.time_of_day))
      : '',
    pref_virtual_allowed: typeof virtualAllowed === 'boolean' ? virtualAllowed ? 'Yes' : 'No' : '',
    pref_days_unavailable: stringValue(preferences.days_and_time_that_do_or_do_not_work),
  };
}

export function patientEmailFromFormData(formData: unknown): string {
  if (typeof formData === 'string') {
    try {
      return patientEmailFromFormData(JSON.parse(formData));
    } catch {
      return '';
    }
  }
  if (!formData || typeof formData !== 'object' || Array.isArray(formData)) return '';
  const email = (formData as Record<string, unknown>).email_address;
  return typeof email === 'string' ? email.trim() : '';
}
